"use client";

import { cn } from "@/lib/utils";
import { AuraPresence } from "../aura-presence";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Pause,
  FileSearch,
  Play,
  Hand,
  Users,
  MessageSquare,
  Info,
  User,
  AlertCircle,
  GitBranch,
  Eye,
  ChevronRight,
  CheckCheck,
  XCircle,
} from "lucide-react";
import type {
  AuraBrief,
  ActiveJob,
  ExecutionState,
  WhatMattersNowItem,
  ExecutionReadiness,
  EvidenceConfidence,
  ApprovalRole,
  JobStatus,
  RiskLevel,
  DecisionTopicStatus,
  JobStage,
  DomainState,
} from "@/lib/aura/types";

// ============================================
// Props Interface
// ============================================

interface MainOperationalConsoleProps {
  brief: AuraBrief;
  jobs: ActiveJob[];
  executionState: ExecutionState;
  onReviewEvidence?: () => void;
  onApproveExecution?: () => void;
  onHoldDeployment?: () => void;
  onOpenDecisionRoom?: () => void;
  onCommandAura?: () => void;
  onOpenJob?: (jobId: string) => void;
  onOpenWorktree?: (jobId: string) => void;
  onRequestReview?: (jobId: string) => void;
  onViewEvidence?: (jobId: string) => void;
  onViewAllJobs?: () => void;
  onJobAction?: (jobId: string, action: string) => void;
}

// ============================================
// Helper Components
// ============================================

interface SectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

function Section({ title, children, className }: SectionProps) {
  return (
    <div className={cn("", className)}>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

// ============================================
// What Matters Now Item
// ============================================

function WhatMattersNowIcon({ icon }: { icon: WhatMattersNowItem["icon"] }) {
  const iconClasses = {
    warning: "text-warning",
    clock: "text-warning",
    pause: "text-muted-foreground",
    check: "text-success",
    info: "text-accent",
    alert: "text-destructive",
    user: "text-muted-foreground",
  };

  const icons = {
    warning: AlertTriangle,
    clock: Clock,
    pause: Pause,
    check: CheckCircle2,
    info: Info,
    alert: AlertCircle,
    user: User,
  };

  const Icon = icons[icon];
  return <Icon className={cn("w-4 h-4 mt-0.5 shrink-0", iconClasses[icon])} />;
}

function WhatMattersNowRow({ item }: { item: WhatMattersNowItem }) {
  const priorityClasses = {
    critical: "border-l-destructive",
    high: "border-l-warning",
    medium: "border-l-accent/50",
    low: "border-l-success/50",
  };

  return (
    <div
      className={cn(
        "flex items-start gap-2 pl-2 border-l-2",
        priorityClasses[item.priority]
      )}
    >
      <WhatMattersNowIcon icon={item.icon} />
      <span className="text-sm text-foreground">{item.text}</span>
    </div>
  );
}

// ============================================
// Domain State Badge
// ============================================

function getDomainStatusStyle(status: string): "success" | "warning" | "error" | "neutral" | "accent" {
  const statusMap: Record<string, "success" | "warning" | "error" | "neutral" | "accent"> = {
    complete: "success",
    stable: "success",
    active: "success",
    completed: "success",
    "in-progress": "accent",
    incomplete: "warning",
    paused: "neutral",
    waiting: "neutral",
    blocked: "error",
    error: "error",
    degraded: "warning",
    empty: "neutral",
    unknown: "neutral",
  };
  return statusMap[status] || "neutral";
}

function DomainStateBadge({ state }: { state: DomainState }) {
  const status = getDomainStatusStyle(state.status);
  const statusClasses = {
    success: "bg-success/10 text-success border-success/20",
    warning: "bg-warning/10 text-warning border-warning/20",
    error: "bg-destructive/10 text-destructive border-destructive/20",
    neutral: "bg-muted text-muted-foreground border-border",
    accent: "bg-accent/10 text-accent border-accent/20",
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between px-3 py-2 rounded-md border",
        statusClasses[status]
      )}
    >
      <span className="text-xs">{state.label}</span>
      <span className="text-xs font-medium">{state.statusLabel}</span>
    </div>
  );
}

// ============================================
// Job Status Helpers
// ============================================

function getJobStatusColor(status: JobStatus): string {
  const colors: Record<JobStatus, string> = {
    running: "bg-accent animate-pulse",
    pending: "bg-warning",
    queued: "bg-muted-foreground",
    paused: "bg-muted-foreground",
    completed: "bg-success",
    failed: "bg-destructive",
  };
  return colors[status];
}

function getJobStatusLabel(status: JobStatus): string {
  const labels: Record<JobStatus, string> = {
    running: "Running",
    pending: "Pending",
    queued: "Queued",
    paused: "Paused",
    completed: "Completed",
    failed: "Failed",
  };
  return labels[status];
}

function getRiskBadgeStyle(risk: RiskLevel): string {
  const styles: Record<RiskLevel, string> = {
    low: "bg-success/10 text-success border-success/20",
    medium: "bg-warning/10 text-warning border-warning/20",
    high: "bg-destructive/10 text-destructive border-destructive/20",
    critical: "bg-destructive text-destructive-foreground border-destructive",
  };
  return styles[risk];
}

function getDecisionRoomBadge(status: DecisionTopicStatus | null | undefined) {
  if (!status) return null;
  
  const styles: Record<DecisionTopicStatus, { bg: string; text: string }> = {
    live: { bg: "bg-accent/10 border-accent/20", text: "text-accent" },
    blocked: { bg: "bg-destructive/10 border-destructive/20", text: "text-destructive" },
    "waiting-review": { bg: "bg-warning/10 border-warning/20", text: "text-warning" },
    resolved: { bg: "bg-success/10 border-success/20", text: "text-success" },
  };
  
  const labels: Record<DecisionTopicStatus, string> = {
    live: "Live",
    blocked: "Blocked",
    "waiting-review": "Review",
    resolved: "Resolved",
  };

  const style = styles[status];
  return (
    <span className={cn("text-[10px] px-1.5 py-0.5 rounded border", style.bg, style.text)}>
      {labels[status]}
    </span>
  );
}

function getStageLabel(stage: JobStage): string {
  const labels: Record<JobStage, string> = {
    planning: "Planning",
    implementation: "Implementation",
    testing: "Testing",
    review: "Review",
    deployment: "Deployment",
    verification: "Verification",
    complete: "Complete",
  };
  return labels[stage];
}



// ============================================
// Execution Readiness Component
// ============================================

function ExecutionReadinessPanel({ readiness }: { readiness: ExecutionReadiness }) {
  const levelColors: Record<ExecutionReadiness["level"], string> = {
    ready: "from-success to-success",
    partial: "from-accent to-success",
    blocked: "from-destructive to-warning",
    unknown: "from-muted-foreground to-muted-foreground",
  };

  return (
    <div className="glass-panel rounded-lg p-4 space-y-4">
      {/* Score Display */}
      <div className="text-center">
        <div className="text-4xl font-semibold text-foreground mb-1">
          {readiness.score}<span className="text-2xl text-muted-foreground">%</span>
        </div>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full bg-gradient-to-r", levelColors[readiness.level])}
            style={{ width: `${readiness.score}%` }}
          />
        </div>
      </div>

      {/* Blockers */}
      {readiness.blockers.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-[10px] uppercase tracking-wider text-destructive font-medium">Blockers</h4>
          {readiness.blockers.map((blocker, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <XCircle className="w-3 h-3 text-destructive shrink-0 mt-0.5" />
              <span>{blocker}</span>
            </div>
          ))}
        </div>
      )}

      {/* Safe Actions */}
      {readiness.safeActions.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-[10px] uppercase tracking-wider text-success font-medium">Safe Actions</h4>
          {readiness.safeActions.map((action, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="w-3 h-3 text-success shrink-0 mt-0.5" />
              <span>{action}</span>
            </div>
          ))}
        </div>
      )}

      {/* Blocked Actions */}
      {readiness.blockedActions.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-[10px] uppercase tracking-wider text-warning font-medium">Blocked Actions</h4>
          {readiness.blockedActions.map((action, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Hand className="w-3 h-3 text-warning shrink-0 mt-0.5" />
              <span>{action}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Evidence Confidence Component
// ============================================

function EvidenceConfidencePanel({ confidence }: { confidence: EvidenceConfidence }) {
  const levelColors: Record<EvidenceConfidence["level"], string> = {
    low: "text-destructive",
    medium: "text-warning",
    high: "text-success",
    verified: "text-success",
  };

  return (
    <div className="glass-panel rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Confidence</span>
        <span className={cn("text-sm font-medium capitalize", levelColors[confidence.level])}>
          {confidence.level}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <CheckCheck className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          {confidence.verifiedChecks} of {confidence.totalChecks} checks verified
        </span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {confidence.reason}
      </p>
    </div>
  );
}

// ============================================
// Active Jobs Panel
// ============================================

interface ActiveJobsPanelProps {
  jobs: ActiveJob[];
  onJobAction?: (jobId: string, action: string) => void;
  onViewAllJobs?: () => void;
}

function sortJobsByPriority(jobs: ActiveJob[]): ActiveJob[] {
  const priorityScore = (job: ActiveJob): number => {
    // Lower score = higher priority
    let score = 0;
    
    // 1. Blocked jobs (highest priority)
    if (job.decisionRoomStatus === "blocked") score -= 1000;
    
    // 2. Approval required
    if (job.decisionRoomStatus === "waiting-review") score -= 800;
    
    // 3. High/Critical risk
    if (job.riskLevel === "critical") score -= 600;
    if (job.riskLevel === "high") score -= 500;
    
    // 4. Running jobs
    if (job.status === "running") score -= 400;
    
    // 5. Pending/Queued (waiting review)
    if (job.status === "pending") score -= 200;
    if (job.status === "queued") score -= 100;
    
    // 6. Recently updated (approximate via status)
    if (job.status === "paused") score += 50;
    if (job.status === "completed") score += 100;
    
    return score;
  };

  return [...jobs].sort((a, b) => priorityScore(a) - priorityScore(b));
}

function getJobStatusCounts(jobs: ActiveJob[]): { 
  running: number; 
  blocked: number; 
  waiting: number; 
  completed: number;
  total: number;
} {
  return {
    running: jobs.filter(j => j.status === "running").length,
    blocked: jobs.filter(j => j.decisionRoomStatus === "blocked").length,
    waiting: jobs.filter(j => j.status === "pending" || j.status === "queued").length,
    completed: jobs.filter(j => j.status === "completed").length,
    total: jobs.length,
  };
}

function ActiveJobsPanel({ jobs, onJobAction, onViewAllJobs }: ActiveJobsPanelProps) {
  const sortedJobs = sortJobsByPriority(jobs);
  const counts = getJobStatusCounts(jobs);
  
  // Show only top priority jobs (max 5 by default)
  const displayedJobs = sortedJobs.slice(0, 5);
  const hasMore = jobs.length > 5;

  return (
    <section className="glass-panel rounded-lg overflow-hidden">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-panel/95 backdrop-blur-sm border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Active Jobs
            </h3>
            <span className="text-xs text-muted-foreground">
              {counts.total}
            </span>
          </div>
          
          {/* Status Summary */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-[10px]">
              {counts.running > 0 && (
                <span className="flex items-center gap-1 text-accent">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  Running {counts.running}
                </span>
              )}
              {counts.blocked > 0 && (
                <span className="flex items-center gap-1 text-destructive">
                  <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                  Blocked {counts.blocked}
                </span>
              )}
              {counts.waiting > 0 && (
                <span className="flex items-center gap-1 text-warning">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning" />
                  Waiting {counts.waiting}
                </span>
              )}
              {counts.completed > 0 && (
                <span className="flex items-center gap-1 text-success">
                  <span className="w-1.5 h-1.5 rounded-full bg-success" />
                  Completed {counts.completed}
                </span>
              )}
            </div>
            
            {/* View All Jobs Action */}
            <button
              onClick={onViewAllJobs}
              className="flex items-center gap-1 text-[10px] text-accent hover:text-accent/80 transition-colors"
            >
              <span>Open Codex Queue</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Jobs Container */}
      <div className="max-h-[340px] overflow-y-auto">
        <div className="p-4 space-y-3 pb-6">
          {displayedJobs.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No active jobs
            </div>
          ) : (
            displayedJobs.map((job) => (
              <CompactJobCard key={job.id} job={job} onAction={onJobAction} />
            ))
          )}
          
          {/* Show More Indicator */}
          {hasMore && (
            <button
              onClick={onViewAllJobs}
              className="w-full py-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border/50 rounded-md hover:border-border transition-colors"
            >
              +{jobs.length - 5} more jobs in queue
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================
// Compact Job Card (for constrained panel)
// ============================================

interface CompactJobCardProps {
  job: ActiveJob;
  onAction?: (jobId: string, action: string) => void;
}

function CompactJobCard({ job, onAction }: CompactJobCardProps) {
  return (
    <div className="bg-muted/20 border border-border/50 rounded-lg p-3 hover:border-border transition-colors">
      {/* Top Row: Status, Title, Risk, Decision Room */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className={cn("w-2 h-2 rounded-full shrink-0", getJobStatusColor(job.status))} />
          <h4 className="text-sm font-medium text-foreground truncate">{job.title}</h4>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={cn(
            "text-[9px] px-1.5 py-0.5 rounded border uppercase font-medium",
            getRiskBadgeStyle(job.riskLevel)
          )}>
            {job.riskLevel}
          </span>
          {getDecisionRoomBadge(job.decisionRoomStatus)}
        </div>
      </div>

      {/* Middle Row: Metadata Grid */}
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-2">
        <span className="flex items-center gap-1">
          <User className="w-3 h-3" />
          <span className="capitalize">{job.executor}</span>
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{getStageLabel(job.stage)}</span>
        </span>
        {job.branch && (
          <span className="flex items-center gap-1 truncate">
            <GitBranch className="w-3 h-3 shrink-0" />
            <span className="truncate">{job.branch}</span>
          </span>
        )}
        {job.auditor && (
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            <span className="capitalize">{job.auditor}</span>
          </span>
        )}
      </div>

      {/* Progress (if running) */}
      {job.status === "running" && job.progress !== undefined && (
        <div className="mb-2">
          <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
            <span>Progress</span>
            <span>{job.progress}%</span>
          </div>
          <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent to-success rounded-full transition-all"
              style={{ width: `${job.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Bottom Row: Quick Actions */}
      <div className="flex items-center gap-1 pt-1 border-t border-border/30">
        {job.quickActions.slice(0, 5).map((action) => (
          <button
            key={action.id}
            onClick={() => onAction?.(job.id, action.action)}
            className="text-[10px] px-2 py-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
          >
            {action.label}
          </button>
        ))}
        <span className="flex-1" />
        <span className="text-[10px] text-muted-foreground/60">{job.lastUpdate}</span>
      </div>
    </div>
  );
}

// ============================================
// Approval Role Display
// ============================================

function getApprovalRoleLabel(role: ApprovalRole): string {
  const labels: Record<ApprovalRole, string> = {
    "operations-lead": "Operations Lead",
    "tech-lead": "Tech Lead",
    founder: "Founder",
    security: "Security",
    any: "Any Approver",
  };
  return labels[role];
}

// ============================================
// Main Component
// ============================================

export function MainOperationalConsole({
  brief,
  jobs,
  executionState,
  onReviewEvidence,
  onApproveExecution,
  onHoldDeployment,
  onOpenDecisionRoom,
  onCommandAura,
  onOpenJob,
  onOpenWorktree,
  onRequestReview,
  onViewEvidence,
  onViewAllJobs,
  onJobAction,
}: MainOperationalConsoleProps) {
  // Destructure execution state for use in the component
  const { domainStates } = executionState;
  return (
    <div className="flex-1 overflow-y-auto px-4 pb-24 pt-5 sm:px-5 lg:p-6">
      {/* Header Section */}
      <div className="mb-6 lg:mb-8">
        <div className="mb-5 flex items-start gap-3 lg:mb-6 lg:gap-4">
          <AuraPresence size="md" intensity="normal" />
          <div className="min-w-0">
            <h1 className="mb-1 text-2xl font-semibold leading-tight text-foreground lg:text-xl">
              AURA Operational Brief
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {brief.primaryAssessment}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 xl:gap-6">
        {/* Left Column - Main Sections */}
        <div className="space-y-5 xl:col-span-8 xl:space-y-6">
          {/* What Matters Now */}
          <Section title="What Matters Now">
            <div className="glass-panel rounded-lg p-4 space-y-2.5">
              {brief.whatMattersNow.map((item) => (
                <WhatMattersNowRow key={item.id} item={item} />
              ))}
            </div>
          </Section>

          {/* AURA Belief & Recommendation */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Section title="AURA Belief">
              <div className="glass-panel rounded-lg p-4 border-l-2 border-l-accent">
                <p className="text-sm text-foreground leading-relaxed">
                  {brief.belief}
                </p>
              </div>
            </Section>
            <Section title="AURA Recommendation">
              <div className="glass-panel rounded-lg p-4 border-l-2 border-l-accent">
                <p className="text-sm text-foreground leading-relaxed">
                  {brief.recommendation}
                </p>
              </div>
            </Section>
          </div>

          {/* Execution State (Domain-Agnostic) */}
          <Section title="Execution State">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {domainStates.map((state) => (
                <DomainStateBadge key={state.domain} state={state} />
              ))}
            </div>
          </Section>

          {/* Active Jobs Panel */}
          <ActiveJobsPanel 
            jobs={jobs} 
            onJobAction={onJobAction} 
            onViewAllJobs={onViewAllJobs}
          />
        </div>

        {/* Right Column - Metrics & Actions */}
        <div className="space-y-5 xl:col-span-4 xl:space-y-6">
          {/* Execution Readiness */}
          <Section title="Execution Readiness">
            <ExecutionReadinessPanel readiness={brief.executionReadiness} />
          </Section>

          {/* Evidence Confidence */}
          <Section title="Evidence Confidence">
            <EvidenceConfidencePanel confidence={brief.evidenceConfidence} />
          </Section>

          {/* Required Approval */}
          {brief.requiredApprovalRole && (
            <Section title="Required Approval">
              <div className="glass-panel rounded-lg p-4 border border-warning/20">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-warning" />
                  <span className="text-sm font-medium text-foreground">
                    {getApprovalRoleLabel(brief.requiredApprovalRole)}
                  </span>
                </div>
                {brief.requiredApprovalDescription && (
                  <p className="text-xs text-muted-foreground">
                    {brief.requiredApprovalDescription}
                  </p>
                )}
              </div>
            </Section>
          )}

          {/* Actions */}
          <Section title="Actions">
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-sm"
                onClick={onReviewEvidence}
              >
                <FileSearch className="w-4 h-4" />
                Review Evidence
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-sm border-success/30 text-success hover:bg-success/10 hover:text-success"
                onClick={onApproveExecution}
              >
                <Play className="w-4 h-4" />
                Approve Execution
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-sm border-warning/30 text-warning hover:bg-warning/10 hover:text-warning"
                onClick={onHoldDeployment}
              >
                <Hand className="w-4 h-4" />
                Hold Deployment
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-sm"
                onClick={onOpenDecisionRoom}
              >
                <Users className="w-4 h-4" />
                Open Decision Room
              </Button>
              <Button
                className="w-full justify-start gap-2 text-sm bg-accent/20 text-accent hover:bg-accent/30 border border-accent/30"
                onClick={onCommandAura}
              >
                <MessageSquare className="w-4 h-4" />
                Command AURA
              </Button>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
